import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  alpha,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentsIcon from '@mui/icons-material/Payments';
import moment from 'moment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { HotelDateTimePicker as DateTimePicker } from '../../../shared/components/forms/HotelDatePicker';
import { addHallBookingVendorPayment } from '../api';
import { HALL_EVENT_PAYMENT_METHOD_OPTIONS } from '../utils/hallEventForm';
import { getHallApiErrorMessage } from '../utils/hallErrors';
import { formatHallCurrency, roundHallMoney } from '../utils/hallFinancials';
import {
  getHallDatePickerSlotProps,
  getHallDrawerPaperSx,
  getHallFormInputSx,
} from '../utils/hallDrawerStyles';
import {
  HallVendorPayoutTypeChips,
  HallVendorSettlementRows,
} from './HallVendorSettlementSummary';

const HALL_ACCENT = '#4ce1b6';

export default function HallAddVendorPaymentDrawer({
  open,
  onClose,
  bookingId,
  booking,
  assignment,
  isDark,
  onPaymentAdded,
}) {
  const inputSx = getHallFormInputSx(isDark);
  const dueAmount = roundHallMoney(assignment?.due_amount);
  const contractAmount = roundHallMoney(assignment?.contract_amount);
  const paidAmount = roundHallMoney(assignment?.paid_amount);
  const hasDue = dueAmount > 0.009;

  const [payoutType, setPayoutType] = useState('full');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('cash');
  const [paidAt, setPaidAt] = useState(() => moment());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    const defaultType = dueAmount > 0 ? 'full' : 'partial';
    setPayoutType(defaultType);
    setAmount(dueAmount > 0 ? String(dueAmount) : '');
    setMode('cash');
    setPaidAt(moment());
    setReference('');
    setNote('');
    setError('');
    setSubmitting(false);
  }, [open, dueAmount, assignment?.id]);

  useEffect(() => {
    if (!open) return;
    if (payoutType === 'full' && dueAmount > 0) {
      setAmount(String(dueAmount));
    }
  }, [open, payoutType, dueAmount]);

  const vendorLabel = useMemo(
    () => assignment?.name || 'Vendor',
    [assignment?.name],
  );

  const paymentAmount = payoutType === 'full'
    ? dueAmount
    : roundHallMoney(amount);
  const remainingAfter = Math.max(0, roundHallMoney(dueAmount - paymentAmount));

  const handleSubmit = async () => {
    if (paymentAmount <= 0) {
      setError('Enter a payment amount greater than zero.');
      return;
    }
    if (!bookingId || !assignment?.id) {
      setError('Vendor assignment is missing.');
      return;
    }
    if (dueAmount > 0 && paymentAmount > dueAmount + 0.009) {
      setError(`Payment cannot exceed vendor due (${formatHallCurrency(dueAmount)}).`);
      return;
    }
    if (payoutType === 'partial' && dueAmount > 0 && paymentAmount >= dueAmount - 0.009) {
      setError('Partial payout must be less than the outstanding due. Use Full to settle.');
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      const result = await addHallBookingVendorPayment(bookingId, assignment.id, {
        amount: paymentAmount,
        mode,
        payment_type: payoutType === 'full' ? 'full' : 'partial',
        paid_at: paidAt?.isValid?.() ? paidAt.toISOString() : moment().toISOString(),
        reference: String(reference || '').trim(),
        note: String(note || '').trim(),
      });
      onPaymentAdded?.(result);
      onClose?.();
    } catch (err) {
      setError(getHallApiErrorMessage(err, 'Failed to record vendor payment'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{ sx: getHallDrawerPaperSx(isDark) }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderBottom: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.1 }}>
            <PaymentsIcon sx={{ color: HALL_ACCENT }} />
            <Box>
              <Typography sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a' }}>
                Vendor Payment
              </Typography>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                Pay {vendorLabel} · {booking?.event_name || 'Hall event'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} disabled={submitting} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ px: 2.25, py: 2, flex: 1, overflow: 'auto' }}>
          <Box
            sx={{
              p: 1.5,
              mb: 2,
              borderRadius: 2,
              border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.28) : alpha(HALL_ACCENT, 0.35)}`,
              backgroundColor: isDark ? alpha('#0f172a', 0.55) : '#fff',
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: isDark ? '#f8fafc' : '#0f172a', mb: 1 }}>
              {vendorLabel}
            </Typography>
            {assignment?.task ? (
              <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mb: 1.1, fontWeight: 600 }}>
                {assignment.task}
              </Typography>
            ) : null}
            <Chip
              size="small"
              label={assignment?.include_in_hall === false ? 'Excluded from hall' : 'Included in hall'}
              sx={{
                mb: 1.1,
                fontWeight: 700,
                color: assignment?.include_in_hall === false
                  ? (isDark ? '#fde68a' : '#92400e')
                  : (isDark ? '#bbf7d0' : '#166534'),
                bgcolor: assignment?.include_in_hall === false
                  ? alpha('#f59e0b', isDark ? 0.2 : 0.14)
                  : alpha('#22c55e', isDark ? 0.2 : 0.14),
              }}
            />
            <HallVendorSettlementRows
              contractAmount={contractAmount}
              paidAmount={paidAmount}
              dueAmount={dueAmount}
              isDark={isDark}
            />
          </Box>

          <Stack spacing={1.75}>
            <Box>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
                Payout type
              </Typography>
              <Box sx={{ mb: 0.75 }}>
                <HallVendorPayoutTypeChips
                  value={payoutType}
                  onChange={(next) => {
                    if (!hasDue && next === 'full') return;
                    setPayoutType(next);
                  }}
                  isDark={isDark}
                />
              </Box>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 600 }}>
                {payoutType === 'full'
                  ? 'Settle the full outstanding vendor due. Written to vendor payments.'
                  : 'Pay part of the due now; remainder stays open. Written to vendor payments.'}
              </Typography>
            </Box>

            {payoutType === 'partial' ? (
              <TextField
                label="Amount paid now"
                type="number"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                fullWidth
                sx={inputSx}
                InputProps={{
                  startAdornment: <InputAdornment position="start">INR</InputAdornment>,
                }}
                helperText={
                  hasDue
                    ? `Must be less than due ${formatHallCurrency(dueAmount)}`
                    : 'No outstanding vendor due'
                }
              />
            ) : (
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: `1px solid ${isDark ? alpha(HALL_ACCENT, 0.35) : alpha(HALL_ACCENT, 0.4)}`,
                  backgroundColor: isDark ? alpha(HALL_ACCENT, 0.1) : alpha(HALL_ACCENT, 0.08),
                }}
              >
                <Typography variant="caption" sx={{ color: isDark ? '#bbf7d0' : '#166534', fontWeight: 700 }}>
                  Paying full due: {formatHallCurrency(dueAmount)}
                </Typography>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, px: 0.25 }}>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700 }}>
                Remaining due after payout
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  color: remainingAfter > 0
                    ? (isDark ? '#ffb74d' : '#e65100')
                    : (isDark ? '#86efac' : '#2e7d32'),
                }}
              >
                {formatHallCurrency(remainingAfter)}
              </Typography>
            </Box>

            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label="Paid at"
                value={paidAt}
                onChange={(value) => setPaidAt(value)}
                slotProps={getHallDatePickerSlotProps(isDark, { fullWidth: true, sx: inputSx })}
              />
            </LocalizationProvider>

            <Box>
              <Typography variant="caption" sx={{ color: isDark ? '#94a3b8' : '#64748b', fontWeight: 700, mb: 0.75, display: 'block' }}>
                Payment mode
              </Typography>
              <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                {HALL_EVENT_PAYMENT_METHOD_OPTIONS.map((option) => {
                  const selected = mode === option.value;
                  return (
                    <Chip
                      key={option.value}
                      label={option.label}
                      onClick={() => setMode(option.value)}
                      sx={{
                        fontWeight: 700,
                        cursor: 'pointer',
                        color: selected ? '#04120e' : (isDark ? '#cbd5e1' : '#334155'),
                        bgcolor: selected
                          ? HALL_ACCENT
                          : (isDark ? alpha('#94a3b8', 0.14) : alpha('#64748b', 0.1)),
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>

            <TextField
              label="Reference / txn id"
              value={reference}
              onChange={(event) => setReference(event.target.value)}
              fullWidth
              sx={inputSx}
            />
            <TextField
              label="Note"
              value={note}
              onChange={(event) => setNote(event.target.value)}
              fullWidth
              multiline
              minRows={2}
              sx={inputSx}
            />

            {error ? (
              <Alert severity="error" sx={{ borderRadius: 2 }}>
                {String(error)}
              </Alert>
            ) : null}
          </Stack>
        </Box>

        <Box
          sx={{
            px: 2.25,
            py: 1.75,
            borderTop: `1px solid ${isDark ? alpha('#fff', 0.08) : alpha('#0f172a', 0.08)}`,
            display: 'flex',
            gap: 1.25,
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={onClose}
            disabled={submitting}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || !hasDue}
            sx={{
              textTransform: 'none',
              fontWeight: 800,
              backgroundColor: HALL_ACCENT,
              color: '#04120e',
              '&:hover': { backgroundColor: HALL_ACCENT, opacity: 0.94 },
            }}
          >
            {submitting ? 'Saving...' : 'Record payout'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
